import { WidgetRegistry } from "components/Editor/widgets/registry";
import {
  FETCH_PAGE_HEALTH,
  UPDATE_ACTIVE_PAGE_HEALTH_CATEGORY,
  UPDATE_PAGE_HEALTH_PERFORMANCE_WARNINGS,
  UPDATE_PAGE_HEALTH_SCORE,
  UPDATE_PAGE_HEALTH_SCORE_CANONICAL,
  UPDATE_PAGE_HEALTH_SEO_WARNINGS,
  UPDATE_PAGE_HEALTH_SHOW_SEO_RECOMMENDATION,
} from "constants/actionTypes";
import {
  LIVE_ELEMENT_COUNT,
  MISSING_ALT_TAGS,
  SOCIAL_DESCRIPTION,
  SOCIAL_IMAGE,
  SOCIAL_TITLE,
  TITLE_LENGTH,
  URL_LENGTH,
  WIDGET_COUNT,
} from "constants/pageHealth";
import {
  CONTENT_CARD,
  CONTENT_CARD_TEMPLATE,
  CONTENT_PAGINATION_BUTTON,
  IMAGE,
  LIVE_ELEMENT,
  PAGE,
  SECTION_COLUMN,
  TEXT,
} from "constants/widgetTypes";
import _ from "lodash";
import { createAction } from "redux-actions";
import { call, select, takeEvery, put } from "redux-saga/effects";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export const fetchPageHealth = createAction(
  FETCH_PAGE_HEALTH,
  (editorSession, storyboardType, pageId, interactive_active_uid) => {
    return {
      editorSession,
      storyboardType,
      pageId,
      interactive_active_uid,
    };
  }
);

export const updatePageHealthPerformanceWarnings = createAction(
  UPDATE_PAGE_HEALTH_PERFORMANCE_WARNINGS,
  (performanceWarnings, widgetCount, liveElementCount, mediaCache) => {
    return {
      performanceWarnings,
      widgetCount,
      liveElementCount,
      mediaCache,
    };
  }
);

export const updatePageHealthSeoWarnings = createAction(
  UPDATE_PAGE_HEALTH_SEO_WARNINGS,
  (seoWarnings) => {
    return {
      seoWarnings,
    };
  }
);

export const updatePageHealthScore = createAction(
  UPDATE_PAGE_HEALTH_SCORE,
  (pageScore) => pageScore
);

export const updatePageHealthScoreCanonicalURL = createAction(
  UPDATE_PAGE_HEALTH_SCORE_CANONICAL,
  (url) => url
);

export const updateActivePageHealthCategory = createAction(
  UPDATE_ACTIVE_PAGE_HEALTH_CATEGORY,
  (category) => {
    return category;
  }
);

export const updatePageHealthShowSEORecommendation = createAction(
  UPDATE_PAGE_HEALTH_SHOW_SEO_RECOMMENDATION,
  (pageId, seoToggle) => {
    return {pageId, seoToggle};
  }
);

function* fetchPageHealthSaga(action) {
  const { editorSession, storyboardType, pageId, interactive_active_uid } =
    action.payload;
  const widgets = yield select((state) =>
    _.get(state, `${editorSession}.present.widgets`, [])
  );
  const appearanceBackgroundURL = yield select((state) =>
    _.get(state, `${editorSession}.present.appearance.background.media.url`, "")
  );
  const slideBackgroundURL = yield select((state) =>
    _.get(
      state,
      `${editorSession}.present.appearance.slideBackground.media.url`,
      ""
    )
  );
  const pageHealth = yield select((state) =>
    _.get(state, "pages.pageHealth", {})
  );
  const page = yield select((state) =>
    _.get(state, `pages.page.${pageId}.item`, {})
  );
  const breakpoint = yield select((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );
  const seoToggle = _.get(page, "seoToggle", false);

  let pageHealthContext = {
    page,
    widgets,
    storyboardType,
    editorSession,
    pageHealth,
    appearanceBackgroundURL,
    slideBackgroundURL,
    interactive_active_uid,
    breakpoint,
  };

  // PERFORMANCE WARNINGS
  const {
    totalSize,
    performanceWarnings,
    widgetCount,
    liveElementCount,
    totalNumberOfImages,
    mediaCache,
  } = yield call(calculatePerformanceWarnings, pageHealthContext);
  yield put(
    updatePageHealthPerformanceWarnings(
      performanceWarnings,
      widgetCount,
      liveElementCount,
      mediaCache
    )
  );

  // SEO WARNINGS
  const {
    seoWarnings,
    missingALTTagsLength,
    hasTitleAndMetaDescription,
    hasSocialMedia,
    hasURLLength,
    hasHeadingStructure,
  } = yield call(calculateSeoWarnings, pageHealthContext);
  yield put(updatePageHealthSeoWarnings(seoWarnings));

  // PERFORMANCE SCORE
  const finalHealthScore = yield call(calculatePageScore, {
    widgetCount,
    liveElementCount,
    totalNumberOfImages,
    missingALTTagsLength,
    totalSize,
    hasTitleAndMetaDescription,
    hasSocialMedia,
    hasURLLength,
    hasHeadingStructure,
    seoToggle,
  });

  yield put(updatePageHealthScore(finalHealthScore));
}

export default function* pageHealthSaga() {
  yield takeEvery(FETCH_PAGE_HEALTH, fetchPageHealthSaga);
}

/** PERFORMANCE */

function getWidgetCountDetails(count) {
  return {
    title: "Widget Count",
    warningTag: count,
    ignored: false,
    criticality: count > 1000 ? "high" : count > 800 ? "medium" : "low",
    message: `<p>You have more widgets than recommended <span>400</span>. Reducing them can improve page performance.</p>`,
    type: WIDGET_COUNT,
  };
}

function getLiveElementCountDetails(count) {
  return {
    title: "Live Element Count",
    warningTag: count,
    ignored: false,
    criticality:
      count > 10 ? "high" : count > 7 ? "medium" : count > 5 ? "low" : "",
    message: `<p>Your page has more than <span>5</span> live elements. Try reducing them to improve performance.</p>`,
    type: LIVE_ELEMENT_COUNT,
  };
}

// CALCULATE PERFORMANCE WARNINGS
async function calculatePerformanceWarnings(pageHealthContext) {
  try {
    const {
      widgets,
      storyboardType,
      pageHealth,
      appearanceBackgroundURL,
      slideBackgroundURL,
      breakpoint,
    } = pageHealthContext || {};
    const performanceWarnings = [];
    const mediaCache = _.get(pageHealth, "mediaCache", new Map());

    let totalSize = 0.704;
    let totalNumberOfImages = 0;
    let numberOfLiveElements = 0;
    let widgetCount = 0;

    // GET ALL WIDGET SIZES [IMAGES, VIDEOS, TEXT, BUTTON]
    const widgetSizes = await Promise.all(
      widgets.map(async (widget) => {
        const visible = findVisibleParent(widgets, widget, breakpoint);

        if (!visible) return 0;

        widgetCount++;

        if (widget.type === LIVE_ELEMENT) {
          numberOfLiveElements++;
        }

        if (widget.type === IMAGE) {
          totalNumberOfImages++;
        }

        const result = await WidgetRegistry.sizeConfig(widget, mediaCache);

        if (result === undefined || result?.size === undefined) return 0;

        return result.size;
      })
    );

    // ADD ALL WIDGET SIZES
    totalSize += widgetSizes.reduce((sum, size) => sum + size, 0);

    // GET SPATIAL AND INTERACTIVE CONTENT BACKGROUND SIZES
    if (["SPATIAL", "INTERACTIVE_CONTENT"].includes(storyboardType)) {
      if (appearanceBackgroundURL) {
        const spatialBackgroundSize = await getMediaInfo(
          `${IMAGE_CDN_ENDPOINT}/${appearanceBackgroundURL}`,
          mediaCache
        );
        if (spatialBackgroundSize) {
          totalNumberOfImages++;
          totalSize += spatialBackgroundSize;
        }
      }
      if (slideBackgroundURL) {
        const slideBackgroundSize = await getMediaInfo(
          `${IMAGE_CDN_ENDPOINT}/${slideBackgroundURL}`,
          mediaCache
        );
        if (slideBackgroundSize) {
          totalNumberOfImages++;
          totalSize += slideBackgroundSize;
        }
      }
    }

    if (widgetCount > 400) {
      performanceWarnings.push(getWidgetCountDetails(widgetCount));
    }

    if (numberOfLiveElements > 5) {
      performanceWarnings.push(
        getLiveElementCountDetails(numberOfLiveElements)
      );
    }

    return {
      performanceWarnings,
      widgetCount,
      liveElementCount: numberOfLiveElements,
      totalNumberOfImages,
      totalSize,
      mediaCache,
    };
  } catch (error) {
    return {
      performanceWarnings: [],
      widgetCount: 0,
      liveElementCount: 0,
      totalNumberOfImages: 0,
      totalSize: 0,
      mediaCache: new Map(),
    };
  }
}

// SEO

// CALCULATE SEO WARNINGS
async function calculateSeoWarnings(pageHealthContext) {
  try {
    let {
      page,
      widgets,
      storyboardType,
      interactive_active_uid,
      breakpoint,
      pageHealth
    } = pageHealthContext || {};

    const seoWarnings = [];

    let socialMedia = _.get(page, "draft", {});

    let URLLength = ((_.get(pageHealth, "canonical", "") ?? "") || (_.get(page, "canonical", "") ?? ""))?.length - 1;

    if (storyboardType === "INTERACTIVE_CONTENT") {
      const pages = () => {
        var pages = widgets.filter((item) => item.type === PAGE);
        pages = _.sortBy(pages, "order");
        return pages;
      };
      const interactivePage = pages().find(
        (item) => item.uid === interactive_active_uid
      );
      page = interactivePage || pages()?.[0];
      socialMedia = _.get(page, "properties.desktop", {}) ?? {};

      URLLength = (_.get(socialMedia, "alias", "") ?? "")?.length - 1;
    }

    // TITLE & META DESCRIPTION
    const titleLength = ((_.get(socialMedia, "seo.name", "") || _.get(page, "name", "")) ?? "")?.length;
    const titleMetaDescriptionLength = (
      _.get(socialMedia, "seo.description", "") ?? ""
    )?.length;

    const titleMetaTagsLength = (_.get(socialMedia, "seo.keywords", []) ?? [])
      ?.length;

    // SOCIAL MEDIA
    const socialNameLength =
      (_.get(socialMedia, "social.name", "") ?? "")?.length || titleLength;
    const socialDescriptionLength =
      (_.get(socialMedia, "social.description", "") ?? "")?.length ||
      titleMetaDescriptionLength;
    const socialImageLength = (_.get(socialMedia, "social.icon.url", "") ?? "")
      ?.length;

    // TWITTER
    const twitterNameLength =
      (_.get(socialMedia, "twitter.name", "") ?? "")?.length ||
      socialNameLength ||
      titleLength;
    const twitterDescriptionLength =
      (_.get(socialMedia, "twitter.description", "") ?? "")?.length ||
      socialDescriptionLength ||
      titleMetaDescriptionLength;
    const twitterImageLength =
      (_.get(socialMedia, "twitter.icon.url", "") ?? "")?.length ||
      socialImageLength;

    const hasTitleAndMetaDescription =
      titleLength <= 60 &&
      titleLength > 0 &&
      titleMetaDescriptionLength <= 150 &&
      titleMetaDescriptionLength > 0 &&
      !!titleMetaTagsLength;
    const hasSocialMedia =
      socialNameLength <= 60 &&
      socialNameLength > 0 &&
      socialDescriptionLength <= 150 &&
      socialDescriptionLength > 0 &&
      !!socialImageLength &&
      twitterNameLength <= 60 &&
      twitterNameLength > 0 &&
      twitterDescriptionLength <= 150 &&
      twitterDescriptionLength > 0 &&
      !!twitterImageLength;
    const hasURLLength = URLLength <= 60 && URLLength > 0;
    // const hasTextToHTML = getTextToHtmlRatio();
    let hasHeadingStructure = false;

    if (URLLength > 60) {
      seoWarnings.push({
        title: "URL Length",
        warningTag: `${URLLength} characters`,
        ignored: false,
        criticality: "high",
        message: `<p>Your URL is too long <span>(over 60 characters)</span> characters. Try shortening it for better readability and SEO performance.</p>`,
        type: URL_LENGTH,
      });
    }

    if (URLLength <= 0) {
      seoWarnings.push({
        title: "Missing URL",
        warningTag: `Add a URL`,
        ignored: false,
        criticality: "high",
        message: `<p>No URL found. Add a URL to ensure your page can be accessed.</p>`,
        type: URL_LENGTH,
      });
    }

    if (titleLength > 60) {
      seoWarnings.push({
        title: "Title Length",
        warningTag: `${titleLength} characters`,
        ignored: false,
        criticality: "high",
        message: `<p>Your title exceeds the recommended limit <span>(over 60 characters)</span>. Shorten it to improve search visibility.</p>`,
        type: TITLE_LENGTH,
      });
    }

    if (titleLength <= 0) {
      seoWarnings.push({
        title: "Missing Title",
        warningTag: `Add a Title`,
        ignored: false,
        criticality: "high",
        message: `<p>Your page is missing a title. Add one to improve search ranking and user engagement.</p>`,
        type: TITLE_LENGTH,
      });
    }

    if (!titleMetaTagsLength) {
      seoWarnings.push({
        title: "Missing Meta Tags",
        warningTag: `Add Meta Keywords`,
        ignored: false,
        criticality: "low",
        message: `<p>No meta tags found. Add relevant tags to optimize search performance.</p>`,
        type: TITLE_LENGTH,
      });
    }

    if (titleMetaDescriptionLength > 150) {
      seoWarnings.push({
        title: "Meta Description Length",
        warningTag: `${titleMetaDescriptionLength} characters`,
        ignored: false,
        criticality: "medium",
        message: `<p>Your meta description is too long <span>(over 150 characters)</span>. Try making it more concise for better search indexing.</p>`,
        type: TITLE_LENGTH,
      });
    }

    if (titleMetaDescriptionLength <= 0) {
      seoWarnings.push({
        title: "Missing Meta Description",
        warningTag: "Add a Description",
        ignored: false,
        criticality: "medium",
        message: `<p>No meta description found. Adding one helps improve search visibility.</p>`,
        type: TITLE_LENGTH,
      });
    }

    // SOCIAL MEDIA
    if (socialNameLength > 60) {
      seoWarnings.push({
        title: "Social Name Length",
        warningTag: `${socialNameLength} characters`,
        ignored: false,
        criticality: "high",
        message: `<p>Your social media title is too long <span>(over 60 characters)</span>. Keep it concise for better sharing performance.</p>`,
        type: SOCIAL_TITLE,
      });
    }

    if (socialNameLength <= 0) {
      seoWarnings.push({
        title: "Missing Social Name",
        warningTag: `Add a Social Name`,
        ignored: false,
        criticality: "high",
        message: `<p>No social name found. Adding one ensures proper branding on shared links.</p>`,
        type: SOCIAL_TITLE,
      });
    }

    if (socialDescriptionLength > 150) {
      seoWarnings.push({
        title: "Social Description Length",
        warningTag: `${socialDescriptionLength} characters`,
        ignored: false,
        criticality: "medium",
        message: `<p>Your social description is too long <span>(over 150 characters)</span>. Shorten it to ensure key information is visible.</p>`,
        type: SOCIAL_DESCRIPTION,
      });
    }

    if (socialDescriptionLength <= 0) {
      seoWarnings.push({
        title: "Missing Social Description",
        warningTag: "Add a Description",
        ignored: false,
        criticality: "medium",
        message: `<p>No social media description found. Add one to enhance link previews.</p>`,
        type: SOCIAL_DESCRIPTION,
      });
    }

    if (!socialImageLength) {
      seoWarnings.push({
        title: "Social Image Missing",
        warningTag: `Add a Social Image`,
        ignored: false,
        criticality: "high",
        message: `<p>No social media image detected. Adding an image improves link previews when shared.</p>`,
        type: SOCIAL_IMAGE,
      });
    }

    // TWITTER MEDIA
    if (twitterNameLength > 60) {
      seoWarnings.push({
        title: "Twitter Name Length",
        warningTag: `${twitterNameLength} characters`,
        ignored: false,
        criticality: "high",
        message: `<p>Your Twitter title length exceeds the recommended limit <span>(over 60 characters)</span>. Try keeping it short and impactful.</p>`,
        type: SOCIAL_TITLE,
      });
    }

    if (twitterNameLength <= 0) {
      seoWarnings.push({
        title: "Missing Twitter Name",
        warningTag: "Add a Twitter Name",
        ignored: false,
        criticality: "high",
        message: `<p>No Twitter name found. Add one for better link sharing on X (formerly Twitter).</p>`,
        type: SOCIAL_TITLE,
      });
    }

    if (twitterDescriptionLength > 150) {
      seoWarnings.push({
        title: "Twitter Description Length",
        warningTag: `${twitterDescriptionLength} characters`,
        ignored: false,
        criticality: "medium",
        message: `<p>Your Twitter description is too long <span>(over 150 characters)</span>. Shorten it for better visibility in tweets.</p>`,
        type: SOCIAL_DESCRIPTION,
      });
    }

    if (twitterDescriptionLength <= 0) {
      seoWarnings.push({
        title: "Missing Twitter Description",
        warningTag: "Add a Description",
        ignored: false,
        criticality: "medium",
        message: `<p>No Twitter description found. Add one for better engagement when shared.</p>`,
        type: SOCIAL_DESCRIPTION,
      });
    }

    if (!twitterImageLength) {
      seoWarnings.push({
        title: "Twitter Image Missing",
        warningTag: "Add Twitter Image",
        ignored: false,
        criticality: "high",
        message: `<p>Your page is missing a Twitter preview image. Add one for better social sharing.</p>`,
        type: SOCIAL_IMAGE,
      });
    }

    // ALT TAGS
    // Function to get all child widgets recursively
    const getAllChildWidgets = (widgets, parentUid) => {
      return widgets.reduce((acc, widget) => {
        if (widget.parent === parentUid) {
          acc.push(widget, ...getAllChildWidgets(widgets, widget.uid));
        }
        return acc;
      }, []);
    };
    
    // If INTERACTIVE_CONTENT, get only relevant widgets
    if (storyboardType === "INTERACTIVE_CONTENT") {
      widgets = getAllChildWidgets(widgets, interactive_active_uid);
    }
    
    const missingALTTags = widgets.filter((widget) => {
      const hasALT = _.get(widget, "properties.desktop.alt", "");
      const widgetContent = _.get(widget, "content", "");
    
      // Check if the widget has an H1 tag (Heading Structure Penalty)
      if (widget.type === TEXT) {
        const regex = /^<h1[^>]*>[\s\S]*<\/h1>$/i;
        if (regex.test(widgetContent)) {
          hasHeadingStructure = true;
        }
      }
    
      // Check for missing ALT tags (for images only)
      if (!hasALT && !widget?.parameter && widget.type === IMAGE) {
        const visible = findVisibleParent(widgets, widget, breakpoint, "image");
    
        if(!visible) return false;

        seoWarnings.push({
          title: widget.name,
          warningTag: "Add ALT Tag",
          ignored: false,
          criticality: "medium",
          message: `<p>Missing ALT Tag for ${widget.name || "image"}. <span>Add an ALT Tag</span> for better SEO.</p>`,
          widget_uid: widget.uid,
          type: MISSING_ALT_TAGS,
        });
        return true;
      }
    });

    if (!hasHeadingStructure) {
      seoWarnings.push({
        title: "Heading Structure",
        warningTag: `Add H1 Tag`,
        ignored: false,
        criticality: "high",
        message: `<p>You are missing an <span>H1 Tag</span>. Try adding one for better SEO.</p>`,
      });
    }

    return {
      seoWarnings,
      missingALTTagsLength: missingALTTags?.length || 0,
      hasTitleAndMetaDescription,
      hasSocialMedia,
      hasURLLength,
      hasHeadingStructure,
    };
  } catch (error) {
    return {
      seoWarnings: [],
      missingALTTagsLength: 0,
      hasTitleAndMetaDescription: false,
      hasSocialMedia: false,
      hasURLLength: false,
      hasHeadingStructure: false,
    };
  }
}

const findVisibleParent = (widgets, widget, breakpoint, type) => {
  // Check if widget is visible at the current breakpoint
  if(widget.type === PAGE) return true;

  if (!_.get(widget, `properties.${breakpoint}.visible`)) return false;

  // Check if widget has a parent
  if (!widget.parent) return true;

  const parentWidget = widgets.find((w) => w.uid === widget.parent);

  // Check if parent widget 
  if (!parentWidget) return true;

  if(type === 'image') {
    // Check if parent widget is a content card template
    if (parentWidget.type === CONTENT_CARD_TEMPLATE) return false;
  }

  // Check if section column, content card, or content pagination
  if (
    SECTION_COLUMN === widget.type &&
    CONTENT_CARD === widget.type &&
    CONTENT_PAGINATION_BUTTON === widget.type
  )
    return findVisibleParent(widgets, parentWidget, breakpoint, type);

  // Check if parent widget is visible at the current breakpoint
  if (_.get(parentWidget, `properties.${breakpoint}.visible`))
    return findVisibleParent(widgets, parentWidget, breakpoint, type);

  return findVisibleParent(widgets, parentWidget, breakpoint, type); // Recursively check the next parent
};

// GET TOTAL PAGE SCORE
function calculatePageScore({
  widgetCount,
  liveElementCount,
  totalNumberOfImages,
  missingALTTagsLength,
  totalSize,
  hasTitleAndMetaDescription,
  hasSocialMedia,
  hasURLLength,
  hasHeadingStructure,
  seoToggle,
}) {
  try {
    const widgetScoreWeight = 0.2;
    const pageScoreWeight = 0.8;
    const widgetScore = 100 - 100 / (1 + Math.exp(-0.01 * (widgetCount - 800)));
    const pageSize = 100 - 100 / (1 + Math.exp(-1.7 * (totalSize - 22)));
    const initialHealthScore =
      (widgetScore * widgetScoreWeight + pageSize * pageScoreWeight) /
      (widgetScoreWeight + pageScoreWeight);

    // CHECK PENALTY
    const URLLengthPenalty =
      hasURLLength === false ? (0.1 * initialHealthScore) / 6 : 0;
    const TitleAndMetaDescriptionPenalty =
      hasTitleAndMetaDescription === false ? (0.1 * initialHealthScore) / 6 : 0;
    const headingStructurePenalty =
      hasHeadingStructure === false ? (0.1 * initialHealthScore) / 6 : 0;
    const socialMediaPenalty =
      hasSocialMedia === false ? (0.1 * initialHealthScore) / 6 : 0;
    const altImagesPenalty =
      ((0.1 * initialHealthScore) / 6) *
      isNaN(missingALTTagsLength / totalNumberOfImages)
        ? 0
        : missingALTTagsLength / totalNumberOfImages;
    const liveElementsPenalty =
      liveElementCount <= 5
        ? 0
        : liveElementCount <= 7
        ? 0.05 * initialHealthScore
        : liveElementCount <= 10
        ? 0.1 * initialHealthScore
        : 0.2 * initialHealthScore;
    const binaryElementsPenalty =
      URLLengthPenalty +
      TitleAndMetaDescriptionPenalty +
      headingStructurePenalty +
      socialMediaPenalty +
      altImagesPenalty;

    const dampingFactor = initialHealthScore <= 20 ? 0.5 : 1;

    const finalHealthScore =
      initialHealthScore -
      dampingFactor *
        (liveElementsPenalty + (seoToggle ? binaryElementsPenalty : 0));

    return finalHealthScore;
  } catch {
    return 0;
  }
}

/** GET IMAGE SIZE */
export async function getMediaInfo(imageURL, mediaCache) {
  try {
    // Check if image is already in cache
    if (mediaCache.has(imageURL)) {
      return mediaCache.get(imageURL);
    }

    // Get the image size using fetch and HEAD request
    const response = await fetch(imageURL, {
      headers: {
        Accept: "image/webp,image/apng,image/*,*/*;q=0.8",
        "User-Agent": navigator.userAgent, // Mimic browser user agent
      },
      cache: "force-cache", // Force cache
    });
    const blob = await response.blob();
    const sizeBytes = blob.size;
    const sizeKB = sizeBytes / 1024;
    let sizeMB = sizeKB / 1024;

    mediaCache.set(imageURL, sizeMB);

    return sizeMB;
  } catch (error) {
    console.error("Error fetching image details:", error);
  }
}
